import { useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { message } from 'antd';
import { UploadFile } from 'antd/es/upload';
import axios from 'axios';
import { useConfig } from 'providers';
import { useCallback, useMemo, useRef, useState } from 'react';
import { GetFile, SortOrder, useAccessToken } from 'services';

type UseUploadProps = {
  onSuccess?: (file: GetFile['file']) => void;
};

export const useUpload = (config?: UseUploadProps) => {
  const { onSuccess } = config || {};
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();
  const controllerRef = useRef(new AbortController());

  const { mdcFullApiUrl } = useConfig();
  // const {
  //   folderId,
  //   page,
  //   perPage,
  //   query,
  //   orderByField = 'id',
  //   direction = SortOrder.Desc,
  // } = useSearch({ from: '/_app/files/' });

  // const orderBy = useMemo(() => [{ column: orderByField, order: direction }], [orderByField, direction]);

  const handleUpload = useCallback(
    (file: any, folderId?: string | number) => {
      const formData = new FormData();

      formData.append('file', file);

      if (folderId) {
        formData.append('folder_id', folderId.toString());
      }
      setIsUploadingFile(true);

      const currentController = new AbortController();

      controllerRef.current = currentController;

      axios<GetFile['file']>({
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
        method: 'post',
        url: `${mdcFullApiUrl}files`,
        signal: currentController.signal,
        data: formData,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;

          if (total) {
            setUploadProgress(Math.round((loaded / total) * 100));
          }
        },
      })
        .then((response) => {
          if (response.data) {
            onSuccess?.(response.data);
          }
          setIsUploadingFile(false);
          setUploadProgress(null);
          queryClient.invalidateQueries({ queryKey: ['getFiles.infinite'], exact: false });
          queryClient.invalidateQueries({ queryKey: ['getPaginatedFiles'], exact: false });
          queryClient.invalidateQueries({ queryKey: ['filesCardQuery.infinite'], exact: false });
          message.success('File uploaded successfully.');
        })
        .catch(() => {
          setIsUploadingFile(false);
          setUploadProgress(null);
          message.error('File could not upload!');
        });
    },
    [accessToken, mdcFullApiUrl, queryClient, onSuccess],
  );

  const cancelFileUpload = useCallback(() => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
  }, []);

  return {
    handleUpload,
    isUploadingFile,
    uploadProgress,
    cancelFileUpload,
  };
};
